import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { MOODRA_API_KEY, MOODRA_API_URL } from '../config';

// Define the shape of our survey data
interface SurveyState {
  demographics: Record<string, any>;
  answers: Record<string, any>;
  currentStep: string;
  currentSection: number;
  currentSubsection: number;
  hasStarted: boolean;
  hasSeenMidBreak1: boolean;
  hasSeenMidBreak2: boolean;
}

// Define the context shape
interface SurveyContextType {
  surveyState: SurveyState;
  setDemographics: (demographics: Record<string, any>) => void;
  addAnswers: (newAnswers: Record<string, any>) => Promise<void>;
  setCurrentStep: (step: string) => void;
  setCurrentSection: (section: number) => void;
  setCurrentSubsection: (subsection: number) => void;
  startSurvey: () => void;
  resetSurvey: () => void;
  getProgress: () => number;
  getNextStep: () => string;
  setMidBreakSeen: (section: number) => void;
  submitSurvey: (currentAnswers?: any) => Promise<{ success: boolean; error?: string; data?: any }>;
}

// Create the context with a default value
const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

// Local storage keys
const SURVEY_STATE_KEY = 'moodra_survey_state';
const SURVEY_STARTED_KEY = 'moodra_survey_started';

const createInitialState = (): SurveyState => ({
  demographics: {},
  answers: {},
  currentStep: 'beginning',
  currentSection: 0,
  currentSubsection: 0,
  hasStarted: false,
  hasSeenMidBreak1: false,
  hasSeenMidBreak2: false,
});

// Restores an in-progress survey so an accidental refresh doesn't discard answers.
const loadPersistedState = (): SurveyState | null => {
  try {
    const stored = localStorage.getItem(SURVEY_STATE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return parsed?.hasStarted ? { ...createInitialState(), ...parsed } : null;
  } catch {
    return null;
  }
};

// Create the provider component
export const SurveyProvider = ({ children }: { children: ReactNode }) => {
  const [surveyState, setSurveyState] = useState<SurveyState>(() => loadPersistedState() ?? createInitialState());

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (surveyState.hasStarted) {
      localStorage.setItem(SURVEY_STATE_KEY, JSON.stringify(surveyState));
      localStorage.setItem(SURVEY_STARTED_KEY, 'true');
    }
  }, [surveyState]);

  const setDemographics = (demographics: Record<string, any>) => {
    setSurveyState(prevState => ({ ...prevState, demographics }));
  };

  const addAnswers = (newAnswers: Record<string, any>): Promise<void> => {
    return new Promise((resolve) => {
      setSurveyState(prevState => {
        const updatedState = {
          ...prevState,
          answers: {
            ...prevState.answers,
            ...newAnswers,
          },
        };
        
        // Resolve the promise after the state update
        setTimeout(() => resolve(), 0);
        
        return updatedState;
      });
    });
  };

  const setCurrentStep = (step: string) => {
    setSurveyState(prevState => ({ ...prevState, currentStep: step }));
  };

  const setCurrentSection = (section: number) => {
    setSurveyState(prevState => ({ ...prevState, currentSection: section }));
  };

  const setCurrentSubsection = (subsection: number) => {
    setSurveyState(prevState => ({ ...prevState, currentSubsection: subsection }));
  };

  const setMidBreakSeen = (section: number) => {
    if (section === 0) {
      setSurveyState(prevState => ({ ...prevState, hasSeenMidBreak1: true }));
    } else if (section === 1) {
      setSurveyState(prevState => ({ ...prevState, hasSeenMidBreak2: true }));
    }
  };

  const submitSurvey = async (currentAnswers?: any) => {
    try {
      // Use passed answers if provided, otherwise fall back to context state
      const answersToSubmit = currentAnswers || surveyState.answers;
      
      const payload = {
        ...surveyState.demographics,
        answers: answersToSubmit,
        surveyVersion: '1.0',
        submittedAt: new Date().toISOString(),
      };

      // Use the API URL from config
      const apiUrl = `${MOODRA_API_URL}/api/generate-report`;

      // Make the API call to your backend
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'x-api-key': MOODRA_API_KEY
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();

      return { success: true, data: result };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  };

  const startSurvey = () => {
    setSurveyState(prevState => ({ 
      ...prevState, 
      // A new run must not inherit a restored run's answers, or a stop would
      // report sections the user hasn't actually completed this time.
      answers: {},
      hasStarted: true, 
      currentStep: 'section-1',
      currentSection: 0,
      currentSubsection: 0,
      hasSeenMidBreak1: false,
      hasSeenMidBreak2: false
    }));
  };

  const resetSurvey = () => {
    localStorage.removeItem(SURVEY_STATE_KEY);
    localStorage.removeItem(SURVEY_STARTED_KEY);
    setSurveyState(createInitialState());
  };

  const getProgress = (): number => {
    // Calculate overall progress across all sections
    if (!surveyState.answers || Object.keys(surveyState.answers).length === 0) {
      return 0;
    }

    // This will be calculated in the QuestionSection component
    return 0;
  };

  const getNextStep = (): string => {
    // Determine the next step based on current state
    if (surveyState.currentStep === 'beginning') return 'beginning-2';
    if (surveyState.currentStep === 'beginning-2') return 'start';
    if (surveyState.currentStep === 'start') return 'section-1';
    if (surveyState.currentStep === 'section-1') {
      if (surveyState.currentSubsection === 5 && !surveyState.hasSeenMidBreak1) {
        return 'break-1-mid';
      }
      if (surveyState.currentSubsection === 11) return 'break-1';
      return 'section-1';
    }
    if (surveyState.currentStep === 'break-1-mid') return 'section-1';
    if (surveyState.currentStep === 'break-1') return 'section-2';
    if (surveyState.currentStep === 'section-2') {
      if (surveyState.currentSubsection === 5 && !surveyState.hasSeenMidBreak2) {
        return 'break-2-mid';
      }
      if (surveyState.currentSubsection === 11) return 'break-2';
      return 'section-2';
    }
    if (surveyState.currentStep === 'break-2-mid') return 'section-2';
    if (surveyState.currentStep === 'break-2') return 'section-3';
    if (surveyState.currentStep === 'section-3') return 'end';
    return 'end';
  };

  return (
    <SurveyContext.Provider value={{ 
      surveyState, 
      setDemographics, 
      addAnswers, 
      setCurrentStep, 
      setCurrentSection, 
      setCurrentSubsection,
      startSurvey,
      resetSurvey,
      getProgress,
      getNextStep,
      setMidBreakSeen,
      submitSurvey
    }}>
      {children}
    </SurveyContext.Provider>
  );
};

// Custom hook to use the survey context
export const useSurveyContext = () => {
  const context = useContext(SurveyContext);
  if (context === undefined) {
    throw new Error('useSurveyContext must be used within a SurveyProvider');
  }
  return context;
}; 